
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Tooltip as LuffyTooltip} from '@baidu/luffy-comp-tooltip';

import {User, USERINFO_TOOLTIP_CARD_TYPE} from '../../interface';
import sdk from '../../sdk/index';
import * as store from '../../store';
import routers, {buildPath} from '../../config/routers';

import Tooltip, {TooltipTrigger} from '../../components/Tooltip';
import Text, {TextType, TextColor} from '../../components/Text';
import Avatar, {AvatarSize} from '../Avatar';
import classNames from 'classnames';
import Anchor, {OpenType, UrlType} from '../Anchor';
import styles from './index.module.scss';
import Icon, {IconSize} from '../Icon';
import {PersonSvg} from '@/icons';
import {openPersonFromUserTooltipIcon} from '@/buried/common';
import kuPiper from '@/middleware/kuPiper';
import {isNull} from 'lodash-es';
import UserCardContent from '../UserCardContent';
import {openUrlOnNewWindow} from '@/utils/browserUtils';
import {isDemissionUser} from '@/utils/user';
import ExternalLabel from '../ExternalLabel';

type Placement = 'top-start' | 'top' | 'top-end' |
            'right-start' | 'right' | 'right-end' |
            'bottom-end' | 'bottom' | 'bottom-start' |
            'left-end' | 'left' | 'left-start' ;
interface UserInfoTooltipProps {
    userGuid: string;
    usersMap?: Map<string, User>;
    // children?: React.ReactNode | React.ReactNode[];
    ignoreAnchor?: boolean;
    onlyAvatar?: boolean;
    withAvatar?: boolean;
    withDepartment?: boolean;
    // withNickNameInTooltip?: boolean;
    className?: string;
    wrapperClass?: string;
    anchorClass?: string;
    avatarClass?: string;
    contentTextClass?: string;
    userInfoTooltipWrapperClass?: string;
    children?: React.ReactChild;
    showPersonalPageIcon?: boolean;
    hideTipAvatar?: boolean;
    showSearchUserCard?: boolean;
    userDesc?: string;
    placement?: Placement;
    isShowJobStatus?: boolean;
    isJumpPersonalHome?: boolean;
    onReady?(userInfo: User): void;
    onPersonalPageIconClick?: () => void;
    onTooltipChange?: (val: boolean) => void;
    onClick?: (event: React.MouseEvent) => void;
    onRlChatClick?: () => void;
    onEmailClick?: () => void;
    docUrl?: string;
}

interface IContent extends UserInfoTooltipProps {
    nickname: string;
    creatorAvatar?: string;
    isJumpPersonalHome?: boolean;
    department: string;
    isExternal: boolean;
    // userName: string;
}

function Content({
    className,
    onlyAvatar,
    withAvatar,
    withDepartment,
    avatarClass,
    userGuid,
    contentTextClass,
    nickname,
    creatorAvatar,
    isJumpPersonalHome,
    department,
    isExternal,
}: IContent) {

    const handleJumpPersonalHome = useCallback((event) => {
        if (!isJumpPersonalHome) {
            return;
        }
        event.stopPropagation();
        const path = buildPath(routers.PersonalHome.path, {
            userName: creatorAvatar || '',
        });
        openUrlOnNewWindow(path);
    }, [creatorAvatar, isJumpPersonalHome]);

    return (
        <div className={classNames(styles.content, className)}>
            {(onlyAvatar || withAvatar) && (
                <Avatar
                    className={classNames(styles.avatar, avatarClass)}
                    userGuid={userGuid}
                    size={AvatarSize.XS}
                />
            )}
            {!onlyAvatar && (
                <div
                    className={`${styles.buttonContainer} ${styles.changeColor}`}
                    onClick={handleJumpPersonalHome}
                >
                    <Text
                        type={TextType.T14}
                        color={TextColor.Primary}
                        className={classNames(styles.contentText, contentTextClass)}
                    >
                        {nickname}
                    </Text>
                    {
                        isExternal ? <ExternalLabel labelName={'外部'} /> : ''
                    }
                    {
                        withDepartment && (
                            <span className={classNames(styles.department, contentTextClass)}>
                                <span className={classNames(styles.departSpace)}>.</span>
                                <span>{department}</span>
                            </span>
                        )
                    }
                </div>
            )}
        </div>
    );
}

export const PLACEMENT: {[index: string]: Placement} = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
};

// eslint-disable-next-line complexity
function UserInfoTooltip(props: UserInfoTooltipProps) {
    const {
        userGuid, usersMap,
        ignoreAnchor = false,
        // withNickNameInTooltip = false,
        withAvatar = false,
        onlyAvatar = false,
        withDepartment = false,
        className,
        wrapperClass,
        anchorClass,
        avatarClass,
        contentTextClass,
        userInfoTooltipWrapperClass,
        children,
        showPersonalPageIcon,
        // 信息卡片要带头像，所以隐藏头像字段从组件内不使用统一展示头像
        hideTipAvatar,
        showSearchUserCard = false,
        isShowJobStatus = false,
        placement = PLACEMENT.bottom,
        onReady,
        onTooltipChange,
        onClick,
        onRlChatClick,
        onEmailClick,
        onPersonalPageIconClick,
        docUrl,
    } = props;

    const [user, setUser] = useState<User | null>(null);
    const noOnJobCont = isDemissionUser(user?.resignStatus);
    const isOnJonText = isShowJobStatus ? noOnJobCont : '';
    const nickname = user?.nickname ? `${user?.nickname}${isOnJonText}` : '';
    const creatorAvatar = user?.avatar || '';
    const department = user?.department || '';
    const isExternal = user?.external || false;
    const passportDisplayName = user?.passportDisplayName || '';

    // const isShowPersonalHome = useMemo(() => {
    //     if (isKMinGray) {
    //         return true;
    //     }
    //     return !!user?.squareUrl;
    // }, [user?.squareUrl]);
    const userPath = useMemo(() => {
        return buildPath(routers.PersonalHome.path, {
            userName: user?.avatar || '',
        });
    }, [user?.avatar]);

    useEffect(() => {
        if (user && user.userGuid === userGuid) {
            return;
        }
        // 因用户信息数据会被缓存 所以当小流量数据未返回前 按照小流量获取
        const userInfo = sdk.as.getUserInfo(userGuid, true);
        if (userInfo) {
            onReady && onReady(userInfo);
            setUser(userInfo);
        } else {
            setUser(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGuid, usersMap]);

    // 卡片内容保持一致，都显示头像
    const isShowAvatar = true;

    const openRlChat = useCallback(e => {
        onRlChatClick && onRlChatClick();
        e.stopPropagation();
    }, [onRlChatClick]);

    const openPersonalHomeUrl = useCallback(e => {
        onPersonalPageIconClick && onPersonalPageIconClick();
        e.stopPropagation();
        // !isKMinGray && openPersonFromUserTooltipIcon();
        // history.push(userPath);
    }, [onPersonalPageIconClick]);

    const onAnchorClick = useCallback(e => {
        e.stopPropagation();
        e.preventDefault();
        onClick && onClick(e);
    }, [onClick]);

    const openEmail = useCallback(e => {
        e.stopPropagation();
        onEmailClick && onEmailClick();
    }, [onEmailClick]);

    const UserCardContentComp = useMemo(() => {
        return user?.canOpenSession && <UserCardContent
            cardType={
                showSearchUserCard ?
                    USERINFO_TOOLTIP_CARD_TYPE.search :
                    USERINFO_TOOLTIP_CARD_TYPE.normal
            }
            // isShowPersonalHome={isShowPersonalHome}
            isShowAvatar={!isShowAvatar}
            avatarClass={avatarClass}
            {...user}
            nickname={user?.nickname ? `${user?.nickname}${noOnJobCont}` : ''}
            openRlChat={openRlChat}
            openEmail={openEmail}
            openPersonalHomeUrl={openPersonalHomeUrl}
            docUrl={docUrl}
            isExternal={isExternal}
            passportDisplayName={passportDisplayName}
        />;
    }, [showSearchUserCard,
        isShowAvatar,
        avatarClass,
        user,
        noOnJobCont,
        openRlChat,
        openEmail,
        openPersonalHomeUrl,
        docUrl,
        passportDisplayName,
        isExternal]);

    function handleTooltipChange(val: boolean) {
        onTooltipChange && onTooltipChange(val);
    }

    // 自定义外部展示
    if (children) {
        return (<LuffyTooltip
            placement={placement as any}
            type={TooltipTrigger.Hover}
            className={classNames(
                styles.tooltip,
                userInfoTooltipWrapperClass,
                !isShowAvatar && styles.tooltipWithAvatar,
                showSearchUserCard && styles.tooltipAllInfo
            )}
            content={UserCardContentComp}
            onChange={handleTooltipChange}
        >
            {children}
        </LuffyTooltip>);
    }

    return (
        <div className={classNames(styles.wrapper, wrapperClass)}>
            <LuffyTooltip
                type={TooltipTrigger.Hover}
                className={classNames(
                    styles.tooltip,
                    userInfoTooltipWrapperClass,
                    !isShowAvatar && styles.tooltipWithAvatar
                )}
                content={UserCardContentComp}
                placement={placement as any}
            >
                <div>
                    {ignoreAnchor && <Content
                        className={className}
                        onlyAvatar={onlyAvatar}
                        withAvatar={withAvatar}
                        withDepartment={withDepartment}
                        avatarClass={avatarClass}
                        userGuid={userGuid}
                        contentTextClass={contentTextClass}
                        nickname={nickname}
                        creatorAvatar={creatorAvatar}
                        isJumpPersonalHome
                        department={department}
                        isExternal={isExternal}
                    />}
                    {!ignoreAnchor && (
                        <div className={classNames(styles.anchorContainer, 'userinfo-tooltip-anchor-container')}>
                            <Anchor
                                className={classNames(
                                    styles.anchor,
                                    anchorClass,
                                    (window.isTocPage || user?.external) && styles.disableClick
                                )}
                                url={userPath}
                                onClick={onAnchorClick}
                                urlType={UrlType.Repository}
                                openType={OpenType.newTab}
                            >
                                <Content
                                    className={className}
                                    onlyAvatar={onlyAvatar}
                                    withAvatar={withAvatar}
                                    withDepartment={withDepartment}
                                    avatarClass={avatarClass}
                                    userGuid={userGuid}
                                    contentTextClass={contentTextClass}
                                    nickname={nickname}
                                    department={department}
                                    isExternal={isExternal}
                                />
                            </Anchor>
                        </div>
                    )}
                </div>
            </LuffyTooltip>
            {
                kuPiper.openNormalReplaceSlot(
                    {
                        scope: kuPiper.slotScopeMap.UserInfoTip,
                        position: kuPiper.slotPositionMap.UserInfoTip.PersonalPageIcon,
                        DefaultComponent: (showPersonalPageIcon) && !isExternal &&
                        <Tooltip
                            type={TooltipTrigger.Hover}
                            content={'打开个人主页'}
                            placement={'top'}
                        >
                            <a
                                href={userPath}
                                onClick={openPersonalHomeUrl}
                                className={styles.personIcon}
                            >
                                <Icon
                                    svg={PersonSvg}
                                    size={IconSize.XS}
                                />
                            </a>
                        </Tooltip>,
                    }
                )
            }
        </div>
    );
}

const UserInfoTooltipImpl = store.connect([
    store.KEYS.USERS_MAP,
])(UserInfoTooltip);

export default UserInfoTooltipImpl;
